/**
 * Every field of Majik's invoices, inspections and messages
 * (docs/majik/DATA-MODEL-V1.md §2.6, §2.8–§2.9). Money is described in
 * dollars, as people see it; the database keeps cents.
 */
import type { FieldDef } from '../types.ts'
import { fieldsOf, standardFields } from './define.ts'

const NY_DAY = 'on New York’s calendar'

export const invoiceFieldDefs: readonly FieldDef[] = [
  ...standardFields('invoice', 'invoice'),
  ...fieldsOf('invoice', [
    ['clientId', 'Client', 'id', 'general', 'The record number of the client billed.'],
    [
      'locationId',
      'Location',
      'id',
      'general',
      'The record number of the site it is for, when it is for one. Always one of the client’s.',
    ],
    [
      'name',
      'Invoice name',
      'text',
      'personal',
      '“<location> | <Month>” for a monthly invoice, “<location> | MM-DD-YYYY” for a one-off.',
    ],
    ['type', 'Type', 'code', 'general', 'A monthly invoice or a one-off.'],
    [
      'billingMonth',
      'Billing month',
      'date',
      'general',
      'The first of the month billed. Required on a monthly invoice.',
    ],
    ['status', 'Status', 'code', 'general', 'Where the invoice stands.'],
    [
      'issuedOn',
      'Invoice date',
      'date',
      'general',
      `The invoice’s own date, ${NY_DAY}; lists sort by it. An imported one keeps Monday’s.`,
    ],
    ['dueDate', 'Due date', 'date', 'general', 'When payment is due.'],
    ['taxCode', 'Tax', 'code', 'general', 'Whether sales tax is added.'],
    ['paymentTerms', 'Payment terms', 'code', 'general', 'How long the client has to pay.'],
    ['readyToSendAt', 'Ready to send', 'datetime', 'general', 'When it was marked ready to send.'],
    [
      'lastSentAt',
      'Sent',
      'datetime',
      'general',
      'When it was last sent. Overdue counts from here.',
    ],
    ['paidAt', 'Paid', 'datetime', 'general', 'When it was paid.'],
    [
      'closedAt',
      'Closed',
      'datetime',
      'general',
      'When it was voided or written off. Empty otherwise.',
    ],
    [
      'statusReason',
      'Status note',
      'text',
      'personal',
      'Why it was voided or written off, in the person’s own words. Optional.',
    ],
    [
      'quickbooksInvoiceId',
      'QB invoice ID',
      'text',
      'operations',
      'The invoice’s id in QuickBooks.',
    ],
    ['invoiceNumber', 'Invoice #', 'text', 'general', 'QuickBooks’ number, e.g. 1026-638060.'],
    [
      'quickbooksTotal',
      'QuickBooks total',
      'money',
      'financial',
      'The total including tax, as QuickBooks worked it out.',
      'dollars',
    ],
    [
      'balanceDue',
      'Balance due',
      'money',
      'financial',
      'What is still owed; 0 means paid. Shows part-payments.',
      'dollars',
    ],
    ['notes', 'Notes', 'text', 'personal', 'Internal notes on the invoice.'],
    [
      'importedUnpaidStale',
      'Old unpaid (imported)',
      'flag',
      'general',
      'Imported from Monday as Sent and already more than 30 days old. Kept as Monday had it; its payment reminders wait until someone confirms it is really unpaid.',
    ],
  ]),
]

export const inspectionFieldDefs: readonly FieldDef[] = [
  ...standardFields('inspection', 'inspection'),
  ...fieldsOf('inspection', [
    [
      'inspectorContactId',
      'Inspector',
      'id',
      'general',
      'The record number of the contact it was sent to. Empty only on an imported inspection Monday named nobody for.',
    ],
    ['status', 'Status', 'code', 'general', 'Whether it has been sent.'],
    ['notes', 'Notes', 'text', 'personal', 'Added to the end of the text.'],
    [
      'sentAt',
      'Sent',
      'datetime',
      'general',
      'When the text went (or would have, before Go live).',
    ],
  ]),
]

export const communicationFieldDefs: readonly FieldDef[] = [
  ...standardFields('communication', 'message'),
  ...fieldsOf('communication', [
    ['channel', 'Channel', 'code', 'general', 'A text, an email, an internal note or an event.'],
    ['direction', 'Direction', 'code', 'general', 'Sent, received, or kept inside Majik.'],
    ['purpose', 'Purpose', 'code', 'general', 'What it was for.'],
    [
      'status',
      'Status',
      'code',
      'general',
      'What happened to it. Before Go live nothing leaves the app: a message is kept as “would send”.',
    ],
    ['title', 'Title', 'text', 'personal', 'For an event or a note: what happened.'],
    ['subject', 'Subject', 'text', 'personal', 'An email’s subject line.'],
    ['body', 'Message', 'text', 'personal', 'The full wording, as sent or received.'],
    ['partyKind', 'To or from', 'code', 'general', 'Who it went to or came from.'],
    [
      'partyContactId',
      'Contact',
      'id',
      'general',
      'The record number of the contact it went to or came from, when known.',
    ],
    [
      'partyName',
      'Name',
      'text',
      'personal',
      'Their name when it went, so it still reads if they are renamed.',
    ],
    [
      'note',
      'How it was sent',
      'text',
      'operations',
      'What drove it, e.g. “Automatic · day-before notice” or “Sent from the app”.',
    ],
    ['at', 'When', 'datetime', 'general', 'When it was sent, received or happened.'],
    [
      'readAt',
      'Read',
      'datetime',
      'general',
      'When someone read an incoming message. Empty while unread. Reading is not a change to the message.',
    ],
    [
      'authorUserId',
      'Written by',
      'person',
      'personal',
      'The person who wrote a note. Empty for messages and events, and for notes imported from Monday.',
    ],
    [
      'authorName',
      'Author',
      'text',
      'personal',
      'A note’s writer as they were named when they wrote it.',
    ],
    [
      'editedAt',
      'Edited',
      'datetime',
      'general',
      'When a note was last changed after it was written.',
    ],
    ['editedByName', 'Edited by', 'text', 'personal', 'Who last changed the note, as named then.'],
    ['parentId', 'Reply to', 'id', 'general', 'The record number of the message this one answers.'],
  ]),
]
